import * as React from 'react';
import {authorize, Overlay} from "@athena-portal/components";
import {lazyInject} from "@athena-portal/core";
import {ICustomerPortalService, MarketingPreference, Party, PartyPropertyEnum, Types} from "@falcon/services";
import FalconFooter from "../../../components/navigation/FalconFooter";
import FalconHeader from "../../../components/navigation/FalconHeader";
import {Link} from "react-router-dom";
import {StorageSuccessModal} from "../../../components/modals/StorageSuccessModal";
import {ConfirmModal} from "@falcon/plugin-falcon-journey";

interface IProps {

}

interface IState {
    party: Party | undefined,
    partyLoaded: boolean,
    backClicked: boolean,
    showOverlay: boolean,
    showSuccessModal: boolean,
    showConfirmChangesModal: boolean,
    preferencesEdited: boolean,
    chkBoxStates: {},
    successModalText: string,
    chosenPreferencesText: string
}

@authorize
export class MarketingPreferencesPage extends React.Component<IProps, IState> {
    readonly checkboxes: any[];

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IProps) {
        super(props);

        this.state = {
            party: undefined,
            partyLoaded: false,
            backClicked: false,
            showOverlay: false,
            showSuccessModal: false,
            showConfirmChangesModal: false,
            preferencesEdited: false,
            successModalText: '',
            chosenPreferencesText: '',
            chkBoxStates: {}
        }

        this.checkboxes = [
            {type: PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL, label: 'Email', className: 'email', text: 'email'},
            {type: PartyPropertyEnum.MARKETING_PREFERENCES_SMS, label: 'SMS', className: 'sms', text: 'sms'},
            {
                type: PartyPropertyEnum.MARKETING_PREFERENCES_PHONE,
                label: 'Phone',
                className: 'phone',
                icon: 'smartphone',
                text: 'phone'
            },
            {type: PartyPropertyEnum.MARKETING_PREFERENCES_POST, label: 'Post', className: 'post', text: 'post'},
        ]

        this.updateMarketingPreferences = this.updateMarketingPreferences.bind(this);
        this.closeSuccessModal = this.closeSuccessModal.bind(this);
        this.openConfirmChangesModal = this.openConfirmChangesModal.bind(this);
    }

    public componentDidMount(): void {
        this.customerPortalService.getProfile().subscribe((output: Party) => {
            const party: Party = output;

            this.setState({
                party,
                partyLoaded: true,
                chkBoxStates: {
                    [PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL),
                    [PartyPropertyEnum.MARKETING_PREFERENCES_SMS]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_SMS),
                    [PartyPropertyEnum.MARKETING_PREFERENCES_PHONE]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_PHONE),
                    [PartyPropertyEnum.MARKETING_PREFERENCES_POST]: this.marketingPreferenceDefault(party, PartyPropertyEnum.MARKETING_PREFERENCES_POST)
                }
            });
        });
    }

    public render(): React.ReactNode {
        if (!this.state.partyLoaded) {
            return <Overlay show={true}/>
        } else if (this.state.party) {
            return <>
                <FalconHeader/>
                <div className="plugin account-details-container marketing-preference-page">
                    <div className="back-link-top">
                        <a onClick={this.openConfirmChangesModal}>
                            <button type="button" role="link" className={`btn btn-back-link`}
                            >Back
                            </button>
                        </a>
                    </div>
                    <div className="text-group title plugin no-indent-desktop">
                        <h1 className={"mb-2"}>Don't miss out, we'd love to keep you up to date</h1>
                    </div>
                    <div>
                        <div className="inner-container marketing-preference">
                            <div className="panel-detail-output-entity text-left">
                                <div className="widget marketing-widget">
                                    <div>
                                        <p className="margin-bottom-32"><span className="font-weight-semi-bold">How would you like to hear about our latest offers,
                                        products and discounts?</span> You can opt in or out at any time from below.</p>
                                    </div>
                                    <div className="checkBox">
                                        {this.renderMarketingPreferenceCheckboxes(this.checkboxes)}
                                    </div>
                                    <div>
                                        <p>Just so you know, if you don’t opt in to our emails, we won’t be able to send
                                            you our newsletter with the latest offers and rewards for being an Allianz
                                            customer such as priority rugby tickets and an exclusive dashcam discount.
                                            Don’t miss out!</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-between button-container w-100">
                            <div className="form-group">
                                <button type="button"
                                        className="btn btn-white-dashboard m-auto"
                                        onClick={this.openConfirmChangesModal}
                                        id="cancelChanges">
                                    Cancel changes
                                </button>
                            </div>
                            <div className="form-group">
                                <button type="button"
                                        className="btn btn-primary m-auto"
                                        onClick={this.updateMarketingPreferences}
                                        id="updateContactDetails"
                                        disabled={!this.preferencesEdited()}>
                                    Save changes
                                </button>
                            </div>
                        </div>

                        <Overlay show={this.state.showOverlay}/>
                        {this.state.showSuccessModal &&
                            <StorageSuccessModal title="Marketing preferences are updated"
                                                 explanationWording={<p>{this.state.successModalText} <span
                                                     className="font-weight-semi-bold">{this.state.chosenPreferencesText}</span>
                                                 </p>}
                                                 onClose={this.closeSuccessModal}
                            />
                        }

                        {<ConfirmModal show={this.state.showConfirmChangesModal}
                                       onHide={this.navigateTo("/dashboard")}
                                       title="You didn’t save your changes"
                                       confirmButtonText="Save changes"
                                       confirmButtonClassName="btn btn-primary width-md-220 mb-3 mt-0 my-md-0"
                                       cancelButtonText="Cancel changes"
                                       cancelButtonClassName="btn btn-text-padded width-md-220 mb-3 mt-0 my-md-0"
                                       onConfirm={this.updateMarketingPreferences}>
                            <div className="fs-20 font-weight-bold font-family-display">
                                Do you want to save the changes you have made to your marketing preferences?
                            </div>
                        </ConfirmModal>}
                    </div>

                    <div className={"back-link-bottom"}>
                        <Link to="#" onClick={this.openConfirmChangesModal}>
                            <button type="button" role="link" className={`btn btn-back-link`}>Back</button>
                        </Link>
                    </div>
                </div>
                <FalconFooter/>
            </>
        }

        return <></>
    }


    private openConfirmChangesModal() {
        if (this.state.preferencesEdited) {
            this.setState({
                showConfirmChangesModal: true,
            });
        } else {
            window.location.href = "/dashboard";
        }
    }

    private renderMarketingPreferenceCheckboxes = (preferences: any[]) => {
        return preferences.map(value => (
            <label className={`${value.className}-preference`}>
                <input type="checkbox"
                       name={value.type}
                       id={value.type}
                       checked={this.state.chkBoxStates[value.type]}
                       onClick={() => this.updateChkBoxState(value.type)}
                />
                <div className="checkbox">
                    <span className={`icon ${value.icon || value.className}-icon`}/>
                    <span className="text font-weight-semi-bold">{value.label}</span>
                </div>
            </label>
        ))
    };

    private readonly marketingPreferenceDefault = (party: Party, preferenceId: string): boolean => {
        let checkboxState: boolean = false;
        if (party && party.customProperties) {
            Object.keys(party.customProperties).forEach(key => {
                if (party && party.customProperties && party.customProperties[key].key === preferenceId) {
                    checkboxState = party.customProperties[key].value === "true";
                }
            })
        }
        return checkboxState;
    }

    private updateChkBoxState = (key: any) => {
        const chkBoxState: boolean = this.state.chkBoxStates[key];
        this.setState(prevState => {
            const updatedChkBox = {...prevState.chkBoxStates};
            updatedChkBox[key] = !chkBoxState;
            return {
                chkBoxStates: updatedChkBox,
                preferencesEdited: true
            };
        })
    };

    private updateMarketingPreferences() {
        this.setState({
            showOverlay: true
        })

        const checkedPreferences: string[] = this.getCheckedMarketingPreferences();
        const marketingPreference: MarketingPreference = this.getMarketingPreference();
        const successModalText = this.getSuccessModalText(checkedPreferences);
        const chosenPreferencesText = this.getChosenPreferencesText(checkedPreferences);

        this.customerPortalService.updateMarketingPreferences(marketingPreference).subscribe(() => {
            this.setState({
                showOverlay: false,
                showSuccessModal: true,
                showConfirmChangesModal: false,
                preferencesEdited: false,
                successModalText,
                chosenPreferencesText
            })
        });
    }

    private getCheckedMarketingPreferences(): string[] {
        const keys: string[] = [PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL, PartyPropertyEnum.MARKETING_PREFERENCES_SMS,
            PartyPropertyEnum.MARKETING_PREFERENCES_PHONE, PartyPropertyEnum.MARKETING_PREFERENCES_POST];

        const checkedPreferences: string[] = [];
        keys.forEach(key => {
            if (this.getCheckboxState(key)) {
                checkedPreferences.push(key);
            }
        })
        return checkedPreferences;
    }

    private getMarketingPreference(): MarketingPreference {
        const marketingPreference: MarketingPreference = {
            emailEnabled: this.getCheckboxState(PartyPropertyEnum.MARKETING_PREFERENCES_EMAIL),
            smsEnabled: this.getCheckboxState(PartyPropertyEnum.MARKETING_PREFERENCES_SMS),
            postEnabled: this.getCheckboxState(PartyPropertyEnum.MARKETING_PREFERENCES_POST),
            phoneEnabled: this.getCheckboxState(PartyPropertyEnum.MARKETING_PREFERENCES_PHONE)
        }
        return marketingPreference;
    }

    private getCheckboxState(elementName: string): boolean {
        const element = document.getElementsByName(elementName)[0] as HTMLInputElement;
        return element.checked;
    }

    private closeSuccessModal(): void {
        this.setState({
            showSuccessModal: false
        })
    }

    private getSuccessModalText(checkedPreferences: string[]): any {
        return (checkedPreferences.length > 0)
            ? "You’ll hear about our latest offers, products and discounts by "
            : "You’ll NOT hear about our latest offers, products and discounts."
    }

    private getChosenPreferencesText(checkedPreferences: string[]): any {
        return (checkedPreferences.length > 0)
            ? this.getCheckedPreferencesType(checkedPreferences)
            : "";
    }

    private getCheckedPreferencesType(checkedPreferencesKeys: string[]): string {
        const checkedCheckboxes = this.checkboxes
            .filter(checkbox => checkedPreferencesKeys.includes(checkbox.type));

        if (checkedCheckboxes.length === 1) {
            return checkedCheckboxes[0].text;
        } else {
            let contactedBy = '';
            checkedCheckboxes.forEach((checkbox, index) => {
                contactedBy = (index !== (checkedCheckboxes.length - 1))
                    ? contactedBy + checkbox.text + ', '
                    : contactedBy + checkbox.text;
            })
            return contactedBy;
        }
    }

    private navigateTo(link: string) {
        return () => {
            window.location.href = link;
        }
    }

    private preferencesEdited(): boolean {
        return this.state.preferencesEdited;
    }
}



// WEBPACK FOOTER //
// ./src/pages/dashboard/common/MarketingPreferencesPage.tsx